import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';

import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { CustomConfigService } from '../../shared-services/custom-config.service';
import { downloadAsExcel } from '@shared-lib/utils/export.utils';

type SeriesMode = 'bar' | 'line';

interface MixedSeriesConfig {
  mode: SeriesMode;
  name: string;
  valueField: string;
  dimensionField: string;
  dimensionValue: string | number;
  yAxisIndex?: number;
}

interface NormalizedInputResult {
  categories: string[];
  recordsByCategory: Record<string, any[]>;
  categoryMetaByCategory: Record<string, { quarter: string; year: string }>;
  isFlatArrayFormat: boolean;
  metaInfo: {
    totalRecords?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  };
}

@Component({
  selector: 'mbar-mline',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mbar-mline.html',
  styleUrls: ['./mbar-mline.css'],
})
export class MbarMlineComponent implements AfterViewInit, OnDestroy {
  @Input() data: any;
  metaInfo: { totalRecords?: number; totalPages?: number; currentPage?: number; pageSize?: number } = {};

  protected myChart!: echarts.ECharts;
  private filterSubscription!: Subscription;
  private resizeObserver: ResizeObserver | null = null;
  private allSeriesNames: string[] = [];
  private currentlySelectedLegends: string[] = [];
  private isInternalLegendChange = false;
  private isCtrlPressed = false;
  private rawChartData: any = null;

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.ctrlKey || event.metaKey) {
      this.isCtrlPressed = true;
    }
  }

  @HostListener('window:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = false;
    }
  }

  constructor(
    protected configService: CustomConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {
    this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.data?.widgetId) return;
      if (this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (!filters?.legendSync) {
          const isGlobalReset = !filters?.widgetId &&
            (!filters?.requestParams || Object.keys(filters.requestParams).length === 0) &&
            (!filters?.queryParams || Object.keys(filters.queryParams).length === 0);

          if (isGlobalReset && !filters?.skipReload) {
            this.currentlySelectedLegends = [];
          }
          this.loadAndRenderChart(filters);
        } else if (filters.legendSync && this.myChart) {
          const isEmptySelection =
            filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0;

          if (isEmptySelection) {
            this.currentlySelectedLegends = [];
            this.selectLegendsByNames([]);
          } else if (this.allSeriesNames.length > 0) {
            const isNameBasedFilter =
              !filters.isCategoryFilter &&
              filters.selectedLegends?.length > 0 &&
              (!filters.selectedIndices || filters.selectedIndices.length === 0);

            if (isNameBasedFilter) {
              this.syncLegendSelectionByName(filters.selectedLegends, filters.sourceWidgetId);
            }
          }
        }
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadAndRenderChart(), 0);
  }

  private loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) {
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }

    const config = this.data?.chartConfig || {};
    const configHeight =
      this.data?.chartHeight ||
      config.chartHeight ||
      config.chartheight ||
      config.height;
    if (configHeight) {
      chartDom.style.height = String(configHeight);
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();

    if (this.data?.data) {
      this.initChart(this.data.data);
      return;
    }

    if (!this.data?.apiUrl) {
      this.myChart.hideLoading();
      this.showNoDataMessage();
      console.warn('No data or apiUrl provided for widget:', this.data?.widgetId);
      return;
    }

    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res) => {
        const chartData = res?.data || res;
        this.initChart(chartData);
      },
      error: (err) => {
        console.error(`[MbarMline] API error for ${this.data?.widgetId}:`, err);
        this.showNoDataMessage();
      },
    });

    this.addResizeListener();
  }

  private initChart(chartData: any): void {
    this.rawChartData = chartData;
    if (!chartData) {
      this.showNoDataMessage();
      return;
    }

    const config = this.data?.chartConfig || {};
    const normalized = this.normalizeInput(chartData);
    this.metaInfo = normalized.metaInfo;
    if (!this.hasRenderableData(normalized)) {
      this.showNoDataMessage();
      return;
    }

    const barMetric = this.data?.barValueField || config.barValueField || 'gross_value';
    const rawLineMetric = this.data?.lineValueField ?? config.lineValueField ?? 'percentage';
    const lineMetric = String(rawLineMetric || '').trim();
    const dimensionField = this.data?.dimensionField || config.dimensionField || 'year';
    const lineSeriesExplicitlyDisabled =
      config.showLines === false ||
      lineMetric.length === 0 ||
      (Array.isArray(config.lineYears) && config.lineYears.length === 0);

    const useSingleMetricSeries =
      normalized.isFlatArrayFormat &&
      (config.useSingleMetricSeries ?? this.data?.useSingleMetricSeries ?? true);

    const barSeries = this.resolveSeriesConfig(
      config.barSeries || [],
      config.barYears,
      'bar',
      barMetric,
      dimensionField,
      config.barNamePrefix || '',
    );
    const lineSeries = lineSeriesExplicitlyDisabled
      ? []
      : this.resolveSeriesConfig(
        config.lineSeries || [],
        config.lineYears,
        'line',
        lineMetric,
        dimensionField,
        config.lineNamePrefix || 'Margin ',
      );

    const fallbackYears = this.resolveUniqueDimensionValues(
      normalized.recordsByCategory,
      dimensionField,
    ).sort((a, b) => Number(b) - Number(a));

    const finalBars = useSingleMetricSeries && !barSeries.length
      ? [
        {
          mode: 'bar' as const,
          name: String(config.barSeriesName || this.data?.barSeriesName || 'Total'),
          valueField: barMetric,
          dimensionField: '__all__',
          dimensionValue: '__all__',
          yAxisIndex: 0,
        },
      ]
      : barSeries.length
        ? barSeries
        : fallbackYears.slice(0, 3).map((year) => ({
          mode: 'bar' as const,
          name: String(year),
          valueField: barMetric,
          dimensionField,
          dimensionValue: year,
          yAxisIndex: 0,
        }));

    const finalLines = lineSeriesExplicitlyDisabled
      ? []
      : useSingleMetricSeries && !lineSeries.length
        ? [
          {
            mode: 'line' as const,
            name: String(config.lineSeriesName || this.data?.lineSeriesName || 'Margin %'),
            valueField: lineMetric,
            dimensionField: '__all__',
            dimensionValue: '__all__',
            yAxisIndex: 1,
          },
        ]
        : lineSeries.length
          ? lineSeries
          : fallbackYears.slice(0, 3).map((year) => ({
            mode: 'line' as const,
            name: `Margin ${year}`,
            valueField: lineMetric,
            dimensionField,
            dimensionValue: year,
            yAxisIndex: 1,
          }));

    const seriesConfig: MixedSeriesConfig[] = [...finalBars, ...finalLines];
    const categories = normalized.categories;
    const enableQuarterYearAxis = config.enableQuarterYearAxis === true;
    const quarterAxisValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.quarter || category,
    );
    const yearAxisRawValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.year || '',
    );
    const yearAxisValues = yearAxisRawValues.map((year, idx) =>
      idx === 0 || year !== yearAxisRawValues[idx - 1] ? year : '',
    );

    const colorPalette: string[] =
      config.colors ||
      this.data?.colors ||
      ['#e53935', '#000000', '#9e9e9e', '#8b0000', '#16a34a', '#1e88e5', '#6b7280', '#ef4444'];

    const barScale = Number(config.barScale || this.data?.barScale || 1000000);
    const barSuffix = String(config.barSuffix || this.data?.barSuffix || 'M');
    const lineSuffix = String(config.lineSuffix || this.data?.lineSuffix || '%');
    const lineDecimals = Number(config.lineDecimals ?? this.data?.lineDecimals ?? 0);

    const series = seriesConfig.map((item, idx) => {
      const dataPoints = categories.map((category) => {
        const rows = normalized.recordsByCategory[category] || [];
        const row =
          item.dimensionField === '__all__'
            ? rows[0]
            : rows.find(
              (r: any) => String(r?.[item.dimensionField]) === String(item.dimensionValue),
            );
        const rawValue = Number(row?.[item.valueField] ?? 0);
        const chartValue =
          item.mode === 'bar' ? rawValue / (barScale || 1) : Number(rawValue.toFixed(lineDecimals));

        return {
          value: chartValue,
          rawValue,
          category,
          quarter: normalized.categoryMetaByCategory?.[category]?.quarter,
          year: normalized.categoryMetaByCategory?.[category]?.year,
          [item.dimensionField]: item.dimensionValue,
          metric: item.valueField,
          series: item.name,
        };
      });

      if (item.mode === 'bar') {
        return {
          name: item.name,
          type: 'bar',
          yAxisIndex: item.yAxisIndex ?? 0,
          xAxisIndex: 0,
          itemStyle: { color: colorPalette[idx % colorPalette.length] },
          barMaxWidth: config.barMaxWidth || 22,
          label:
            config.showBarLabels === false
              ? { show: false }
              : {
                show: true,
                position: 'top',
                formatter: (params: any) => `${Math.round(params.value)}${barSuffix}`,
                fontSize: 10,
                color: '#444',
              },
          data: dataPoints,
        };
      }

      return {
        name: item.name,
        type: 'line',
        yAxisIndex: item.yAxisIndex ?? 1,
        xAxisIndex: 0,
        itemStyle: { color: colorPalette[idx % colorPalette.length] },
        symbol: config.lineSymbol || 'circle',
        symbolSize: config.lineSymbolSize || 6,
        lineStyle: { width: config.lineWidth || 2 },
        label:
          config.showLineLabels === false
            ? { show: false }
            : {
              show: true,
              position: 'top',
              formatter: (params: any) => `${Number(params.value).toFixed(lineDecimals)}${lineSuffix}`,
              fontSize: 10,
              color: '#444',
            },
        data: dataPoints,
      };
    });
    if (!series.length) {
      this.showNoDataMessage();
      return;
    }

    this.allSeriesNames = seriesConfig.map((s) => s.name);

    // Auto-derive legend selection from active year filter in requestParams.
    // Sync carefully with activeYears so table selections map properly to chart legends
    const currentFilters = this.globalFilterService.getCurrentFilters();
    const activeYears = (Array.isArray(currentFilters?.requestParams?.year)
      ? currentFilters.requestParams.year
      : currentFilters?.requestParams?.year
        ? [currentFilters.requestParams.year]
        : []
    ).filter(Boolean).map(String);

    if (activeYears.length > 0) {
      // Check if current legends already successfully reflect the active years
      // so we don't aggressively overwrite manual deselection (e.g., Margin line only toggled)
      const effectiveLegends = this.currentlySelectedLegends.length > 0 ? this.currentlySelectedLegends : this.allSeriesNames;
      const extractedFromLegends = [...new Set(
        effectiveLegends.map(l => {
          const match = String(l || '').match(/(19|20)\d{2}/);
          return match ? match[0] : '';
        }).filter(Boolean)
      )];

      const isAlreadySynced =
        extractedFromLegends.length === activeYears.length &&
        activeYears.every((y: string) => extractedFromLegends.includes(y));

      if (!isAlreadySynced) {
        const prefix = config.lineNamePrefix || 'Margin ';
        const derivedLegends: string[] = [];
        activeYears.forEach((year: string) => {
          if (this.allSeriesNames.includes(year)) {
            derivedLegends.push(year);
          }
          const marginName = `${prefix}${year}`;
          if (this.allSeriesNames.includes(marginName)) {
            derivedLegends.push(marginName);
          }
        });
        // Synchronize internal state with active year filters
        this.currentlySelectedLegends = derivedLegends;
      }
    } else {
      // If no active year filters or all are selected, clear to show all
      // only if we actually need to
      if (this.currentlySelectedLegends.length > 0 && this.currentlySelectedLegends.length < this.allSeriesNames.length) {
        this.currentlySelectedLegends = [];
      }
    }

    const option: echarts.EChartsCoreOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (items: any) => {
          const rows = Array.isArray(items) ? items : [items];
          const title = rows[0]?.axisValueLabel || rows[0]?.name || '';
          const body = rows
            .map((point: any) => {
              const rawValue = point?.data?.rawValue ?? point?.value ?? 0;
              const valueText =
                point.seriesType === 'line'
                  ? `${Number(rawValue).toFixed(lineDecimals)}${lineSuffix}`
                  : `${Math.round(Number(rawValue) / (barScale || 1))}${barSuffix}`;
              return `${point.marker} ${point.seriesName}: ${valueText}`;
            })
            .join('<br/>');
          return `<b>${title}</b><br/>${body}`;
        },
      },
      legend: {
        top: 0,
        left: 'center',
        type: 'scroll',
        data: seriesConfig.map((s) => s.name),
        textStyle: { fontSize: 11 },
        selected: seriesConfig.reduce((acc: any, s: any) => {
          acc[s.name] = this.currentlySelectedLegends.length === 0 || this.currentlySelectedLegends.includes(s.name);
          return acc;
        }, {}),
      },
      grid: {
        top: config.gridTop || 42,
        left: config.gridLeft || 40,
        right: config.gridRight || 48,
        bottom: config.gridBottom || (enableQuarterYearAxis ? 92 : 72),
        containLabel: true,
      },
      xAxis: enableQuarterYearAxis
        ? [
          {
            type: 'category',
            data: quarterAxisValues,
            axisTick: { alignWithLabel: true },
            axisLabel: {
              interval: 0,
              rotate: config.xAxisRotate ?? 0,
              fontSize: 11,
            },
            triggerEvent: true,
          },
          {
            type: 'category',
            position: 'bottom',
            offset: config.yearAxisOffset ?? 22,
            data: yearAxisValues,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: {
              interval: 0,
              rotate: 0,
              fontSize: 11,
              color: '#374151',
              fontWeight: config.yearAxisBold !== false ? 600 : 'normal',
            },
            triggerEvent: true,
          },
        ]
        : {
          type: 'category',
          data: categories,
          axisLabel: {
            interval: 0,
            rotate: config.xAxisRotate ?? 45,
            fontSize: 11,
          },
          triggerEvent: true,
        },
      yAxis: [
        {
          type: 'value',
          name: config.leftAxisName || `Sales ($${barSuffix})`,
          min: 0,
          axisLabel: { formatter: `{value}${barSuffix}` },
          splitLine: { show: false },
        },
        {
          type: 'value',
          name: lineSeriesExplicitlyDisabled ? '' : (config.rightAxisName || 'Margin %'),
          min: config.rightAxisMin ?? 0,
          max: config.rightAxisMax ?? 100,
          axisLabel: { formatter: `{value}${lineSuffix}` },
          splitLine: { show: false },
          show: !lineSeriesExplicitlyDisabled,
        },
      ],
      series: series as any,
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.setupChartClickEvent();
    this.setupLegendClickEvent();

    this.myChart.on('mouseover', (params: any) => {
      if (params.componentType === 'xAxis') {
        this.myChart.getZr().setCursorStyle('pointer');
      }
    });

    this.myChart.on('mouseout', (params: any) => {
      if (params.componentType === 'xAxis') {
        this.myChart.getZr().setCursorStyle('default');
      }
    });
  }

  private hasRenderableData(normalized: NormalizedInputResult): boolean {
    if (!Array.isArray(normalized.categories) || normalized.categories.length === 0) {
      return false;
    }

    return Object.values(normalized.recordsByCategory || {}).some(
      (rows: any[]) => Array.isArray(rows) && rows.length > 0,
    );
  }

  private showNoDataMessage(): void {
    if (!this.myChart) return;

    const config = this.data?.chartConfig || {};
    const noDataText = String(
      this.data?.noDataText || config.noDataText || 'No data is available. Please try changing filters.',
    );

    this.myChart.off('click');
    this.myChart.clear();
    this.myChart.setOption(
      {
        animation: false,
        xAxis: { show: false, type: 'category', data: [] },
        yAxis: { show: false, type: 'value' },
        series: [],
        graphic: {
          type: 'text',
          left: 'center',
          top: 'middle',
          silent: true,
          style: {
            text: noDataText,
            fill: config.noDataTextColor || '#6b7280',
            fontSize: Number(config.noDataFontSize ?? 14),
            fontWeight: config.noDataFontWeight || 500,
            textAlign: 'center',
            textVerticalAlign: 'middle',
          },
        },
      },
      true,
    );
    this.myChart.hideLoading();
  }

  private setupChartClickEvent(): void {
    if (!this.myChart) return;
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      if (params.componentType === 'xAxis') {
        this.chartFilterHandler.handleLabelClick(this.data, params);
        return;
      }

      if (!this.data?.filterKey) return;

      const filterKeys: string[] = Array.isArray(this.data.filterKey)
        ? this.data.filterKey
        : String(this.data.filterKey)
          .split(',')
          .map((k: string) => k.trim())
          .filter(Boolean);

      const payload: any = {};
      const categoryKey = this.data?.categoryKey || this.data?.xAxis || 'category';
      const dimensionField = this.data?.dimensionField || this.data?.chartConfig?.dimensionField || 'year';
      const seriesKey = this.data?.seriesKey || 'series';
      const qtrKeys = new Set(['qtr', 'quarter']);

      filterKeys.forEach((key: string) => {
        if (params?.data && typeof params.data === 'object' && key in params.data) {
          payload[key] = params.data[key];
          return;
        }

        if (key === categoryKey || key === 'category') {
          payload[key] = params?.data?.category ?? params?.name;
          return;
        }

        if (qtrKeys.has(String(key).toLowerCase())) {
          payload[key] = params?.data?.quarter;
          return;
        }

        if (key === dimensionField || key === 'year') {
          payload[key] = params?.data?.year ?? params?.data?.[dimensionField] ?? this.extractYear(params?.seriesName);
          return;
        }

        if (key === seriesKey || key === 'series') {
          payload[key] = params?.seriesName;
        }
      });

      if (!Object.keys(payload).length) return;

      const keyMapping = this.data?.filterKeyMap || this.data?.chartFilterKeyMap || {};
      const mappedPayload: any = {};
      Object.keys(payload).forEach(k => {
        const mappedKey = keyMapping[k] || k;
        mappedPayload[mappedKey] = payload[k];
      });

      // We must also update the filterKey in the data object passed to the filter handler
      // Because the shared chart-filter-handler rigorously checks keys against data.filterKey
      const newData = { ...this.data };
      const originalKeys = Array.isArray(this.data.filterKey)
        ? this.data.filterKey
        : String(this.data.filterKey).split(',').map((k: string) => k.trim());

      newData.filterKey = originalKeys.map((k: string) => keyMapping[k] || k);

      this.chartFilterHandler.updateFilterSelection(newData, mappedPayload);
    });
  }

  private setupLegendClickEvent(): void {
    if (!this.myChart) return;
    this.myChart.off('legendselectchanged');
    this.myChart.on('legendselectchanged', (params: any) => {
      if (this.isInternalLegendChange) return;

      const nativeEvent = params?.event?.event;
      nativeEvent?.preventDefault?.();
      nativeEvent?.stopPropagation?.();

      const clickedLegend = params.name;
      if (!clickedLegend) return;

      const ctrlPressed = (nativeEvent ? !!(nativeEvent.ctrlKey || nativeEvent.metaKey) : false) || this.isCtrlPressed;

      this.isInternalLegendChange = true;
      const relatedLegends = [clickedLegend];

      // If clicking a bar (year), also select the corresponding margin line
      const prefix = this.data?.chartConfig?.lineNamePrefix || 'Margin ';
      if (!clickedLegend.startsWith(prefix)) {
        const year = this.extractYear(clickedLegend);
        if (year) {
          const marginLegend = `${prefix}${year}`;
          if (this.allSeriesNames.includes(marginLegend)) {
            relatedLegends.push(marginLegend);
          }
        }
      }

      if (ctrlPressed) {
        const isAlreadySelected = relatedLegends.every((l) => this.currentlySelectedLegends.includes(l));
        if (isAlreadySelected) {
          this.currentlySelectedLegends = this.currentlySelectedLegends.filter((l) => !relatedLegends.includes(l));
        } else {
          this.currentlySelectedLegends = [...new Set([...this.currentlySelectedLegends, ...relatedLegends])];
        }
      } else {
        const isOnlySelection =
          this.currentlySelectedLegends.length === relatedLegends.length &&
          relatedLegends.every((l) => this.currentlySelectedLegends.includes(l));

        if (isOnlySelection) {
          this.currentlySelectedLegends = [];
        } else {
          this.currentlySelectedLegends = relatedLegends;
        }
      }

      if (this.currentlySelectedLegends.length === 0) {
        this.selectLegendsByNames([]);
        this.updateFilterFromLegends([]);
      } else {
        this.selectLegendsByNames(this.currentlySelectedLegends);
        this.updateFilterFromLegends(this.currentlySelectedLegends);
      }

      setTimeout(() => {
        this.isInternalLegendChange = false;
      }, 50);
    });
  }

  private selectLegendsByNames(names: string[]): void {
    if (!this.myChart) return;
    if (names.length === 0) {
      this.myChart.dispatchAction({ type: 'legendAllSelect' });
    } else {
      const selectedMap: Record<string, boolean> = {};
      this.allSeriesNames.forEach(name => {
        selectedMap[name] = names.includes(name);
      });
      this.myChart.setOption({
        legend: {
          selected: selectedMap
        }
      });
    }
  }

  private syncLegendSelectionByName(selectedLegends: string[], sourceWidgetId: string): void {
    if (sourceWidgetId === this.data.widgetId) return;
    if (!this.myChart || !this.allSeriesNames.length) return;

    const normalizedTargetNames = selectedLegends.map((n) => String(n)?.toUpperCase().trim());
    const matchingNames = this.allSeriesNames.filter(seriesName => {
      const normalizedSeriesName = String(seriesName)?.toUpperCase().trim();
      return normalizedTargetNames.some(targetName =>
        normalizedSeriesName === targetName
      );
    });

    if (matchingNames.length === 0) return;

    this.currentlySelectedLegends = matchingNames;
    this.selectLegendsByNames(matchingNames);
  }

  private updateFilterFromLegends(names: string[]): void {
    const legendListener = this.data?.legendListener || this.data?.legendListner;
    const legendFilterKey = this.data?.legendFilterKey || this.data?.legendFilterkey;
    if (!legendListener) return;

    const key = Array.isArray(legendFilterKey)
      ? legendFilterKey[0]
      : typeof legendFilterKey === 'string'
        ? legendFilterKey.split(',')[0].trim()
        : 'series';

    if (!key) return;

    const cloned = {
      ...this.data,
      filterListener: legendListener,
      filterKey: [key],
    };

    const payload: any = {};
    if (names.length > 0) {
      if (key === 'year') {
        const extractedYears = names.map((name) => this.extractYear(name)).filter(Boolean);
        payload[key] = [...new Set(extractedYears)];
      } else {
        payload[key] = names;
      }
    }

    try {
      const filterData = {
        legendSync: true,
        selectedIndices: [],
        selectedLegends: names,
        selectedColors: [],
        seriesNames: this.allSeriesNames,
        sourceWidgetId: this.data.widgetId,
        filterListener: Array.isArray(legendListener) ? legendListener.join(',') : legendListener,
        isCategoryFilter: key === 'year',
        legendKey: key,
      };
      (this.globalFilterService as any).filterSubject.next(filterData);
    } catch (e) {
      console.warn('Error broadcasting legendSync from mbar-mline:', e);
    }

    this.chartFilterHandler.updateFilterSelection(cloned, names.length > 0 ? payload : []);
  }

  private extractYear(input: any): string {
    const match = String(input || '').match(/(19|20)\d{2}/);
    return match ? match[0] : '';
  }

  private normalizeInput(raw: any): NormalizedInputResult {
    const config = this.data?.chartConfig || {};
    const enableFlatArrayResponse =
      (config.enableFlatArrayResponse ?? this.data?.enableFlatArrayResponse) === true;
    const dataKey = this.data?.dataKey || config.dataKey;
    const categoryFields: string[] = this.data?.categoryFields || config.categoryFields || ['quarter', 'year'];
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? config.categoryDelimiter ?? ' ';
    const quarterField = this.data?.quarterField || config.quarterField || 'quarter';
    const yearField = this.data?.yearField || config.yearField || 'year';

    const data = raw?.data || raw;
    const metaInfo = {
      totalRecords: Number(data?.totalRecords),
      totalPages: Number(data?.totalPages),
      currentPage: Number(data?.currentPage),
      pageSize: Number(data?.pageSize),
    };

    if (enableFlatArrayResponse) {
      const flatData: any[] = Array.isArray(data)
        ? data
        : dataKey && Array.isArray(data?.[dataKey])
          ? data[dataKey]
          : [];

      if (flatData.length > 0) {
        const buildCategory = (row: any): string =>
          categoryFields.map((field) => String(row?.[field] ?? '')).join(categoryDelimiter).trim();

        const quarterOrder = (value: any): number => {
          const match = String(value ?? '').toUpperCase().match(/Q\s*([1-4])/);
          return match ? Number(match[1]) : 0;
        };

        const uniqueCategories = Array.from(new Set(flatData.map((row: any) => buildCategory(row))));
        const categories = uniqueCategories.sort((a: string, b: string) => {
          const rowA = flatData.find((row: any) => buildCategory(row) === a) || {};
          const rowB = flatData.find((row: any) => buildCategory(row) === b) || {};
          const yearA = Number(rowA?.[yearField] ?? 0);
          const yearB = Number(rowB?.[yearField] ?? 0);
          if (yearA !== yearB) return yearA - yearB;
          return quarterOrder(rowA?.[quarterField]) - quarterOrder(rowB?.[quarterField]);
        });

        const recordsByCategory: Record<string, any[]> = {};
        const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
        categories.forEach((category) => {
          const rows = flatData.filter((row: any) => buildCategory(row) === category);
          recordsByCategory[category] = rows;
          const firstRow = rows[0] || {};
          categoryMetaByCategory[category] = {
            quarter: String(firstRow?.[quarterField] ?? ''),
            year: String(firstRow?.[yearField] ?? ''),
          };
        });

        return {
          categories,
          recordsByCategory,
          categoryMetaByCategory,
          isFlatArrayFormat: true,
          metaInfo,
        };
      }
    }

    if (data && typeof data === 'object' && !Array.isArray(data) && !data.category && !data.series) {
      const categories = Object.keys(data).filter((key) => Array.isArray(data[key]));
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat) => {
        recordsByCategory[cat] = Array.isArray(data[cat]) ? data[cat] : [];
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    if (data?.category && data?.series) {
      const categories = Array.isArray(data.category) ? data.category : [];
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat: string, idx: number) => {
        recordsByCategory[cat] = (data.series || []).map((series: any) => ({
          year: series?.name,
          value: Array.isArray(series?.data) ? series.data[idx] : 0,
        }));
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    return {
      categories: [],
      recordsByCategory: {},
      categoryMetaByCategory: {},
      isFlatArrayFormat: false,
      metaInfo,
    };
  }

  private resolveSeriesConfig(
    explicitSeries: any[],
    yearList: Array<string | number>,
    mode: SeriesMode,
    defaultValueField: string,
    defaultDimensionField: string,
    namePrefix: string,
  ): MixedSeriesConfig[] {
    if (Array.isArray(explicitSeries) && explicitSeries.length > 0) {
      return explicitSeries.map((item: any) => ({
        mode,
        name: String(item?.name || `${namePrefix}${item?.dimensionValue ?? ''}`),
        valueField: String(item?.valueField || defaultValueField),
        dimensionField: String(item?.dimensionField || defaultDimensionField),
        dimensionValue: item?.dimensionValue,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    if (Array.isArray(yearList) && yearList.length > 0) {
      return yearList.map((year) => ({
        mode,
        name: mode === 'bar' ? String(year) : `${namePrefix}${year}`,
        valueField: defaultValueField,
        dimensionField: defaultDimensionField,
        dimensionValue: year,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    return [];
  }

  private resolveUniqueDimensionValues(
    recordsByCategory: Record<string, any[]>,
    dimensionField: string,
  ): Array<string | number> {
    const values = new Set<string | number>();
    Object.values(recordsByCategory).forEach((rows: any[]) => {
      rows.forEach((row: any) => {
        if (row?.[dimensionField] !== undefined && row?.[dimensionField] !== null) {
          values.add(row[dimensionField]);
        }
      });
    });
    return Array.from(values);
  }

  private addResizeListener(): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) return;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
    this.resizeObserver.observe(chartDom);
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  hasMetaValue(value: any): boolean {
    return value !== undefined && value !== null && Number.isFinite(Number(value));
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }

  // ─── CSV EXPORT ───────────────────────────────────────────────────────────

  public downloadCsv(): void {
    const rawData = this.rawChartData;
    if (!rawData) return;

    const config = this.data?.chartConfig || {};
    const dataKey = this.data?.dataKey || config.dataKey;
    const barValueField = this.data?.barValueField || config.barValueField || 'gross_value';
    const dimensionField = this.data?.dimensionField || config.dimensionField || 'year';
    const isQuarterly = config.enableQuarterYearAxis === true || (this.data as any).enableQuarterYearAxis === true;

    const inner = rawData?.data || rawData;
    let flatRows: any[] = [];
    if (dataKey && Array.isArray(inner?.[dataKey])) {
      flatRows = inner[dataKey];
    } else if (Array.isArray(inner)) {
      flatRows = inner;
    }

    const dimLabel = (dimensionField || 'year').split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    if (flatRows.length > 0) {
      if (isQuarterly) {
        const grandTotal = flatRows.reduce((s: number, r: any) => s + (Number(r[barValueField]) || 0), 0);
        const headers = ['Year', 'Quarter', '%GT Sum', dimLabel, 'Sum of Total Sales'];
        const csvRows = flatRows.map((r: any) => {
          const val = Number(r[barValueField]) || 0;
          return [
            r['year'] ?? '',
            r['quarter'] ?? r['qtr'] ?? '',
            grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%',
            r[dimensionField] ?? '',
            Math.round(val)
          ];
        });
        this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
      } else {
        const headers = ['Year', dimLabel, 'Sum of Total Sales'];
        const csvRows = flatRows.map((r: any) => [r['year'] ?? r['Year'] ?? '', r[dimensionField] ?? '', Math.round(Number(r[barValueField]) || 0)]);
        this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
      }
      return;
    }

    if (inner && typeof inner === 'object' && !Array.isArray(inner) && !inner.category && !inner.series) {
      const categories = Object.keys(inner).filter(key => Array.isArray(inner[key]));
      if (categories.length > 0) {
        let allRows: any[] = [];
        categories.forEach(cat => {
          inner[cat].forEach((r: any) => {
            allRows.push({ _generatedCategory: cat, ...r });
          });
        });

        let targetCatLabel = dimLabel;
        if (this.data?.categoryKey) {
          targetCatLabel = this.data.categoryKey.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        } else if (dimensionField.toLowerCase() === 'year') {
          targetCatLabel = 'Category';
        }

        const grandTotal = allRows.reduce((s: number, r: any) => s + (Number(r[barValueField]) || 0), 0);

        if (isQuarterly) {
          const headers = ['Year', 'Quarter', '%GT Sum', targetCatLabel, 'Sum of Total Sales'];
          const csvRows = allRows.map((r: any) => {
            const val = Number(r[barValueField]) || 0;
            return [
              r['year'] ?? '',
              r['quarter'] ?? r['qtr'] ?? '',
              grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%',
              r['_generatedCategory'] ?? '',
              Math.round(val)
            ];
          });
          this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
        } else {
          const headers = ['Year', targetCatLabel, 'Sum of Total Sales'];
          const csvRows = allRows.map((r: any) => [
            r['year'] ?? r['Year'] ?? '',
            r['_generatedCategory'] ?? '',
            Number(r[barValueField]) || 0
          ]);
          this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
        }
        return;
      }
    }

    if (inner?.category && inner?.series) {
      const categories: string[] = inner.category;
      const seriesList: any[] = inner.series;

      const extractYear = (s: string) => { const m = String(s).match(/(19|20)\d{2}/); return m ? m[0] : ''; };
      const extractQtr = (s: string) => { const m = String(s).toUpperCase().match(/\bQ\s*([1-4])\b/); return m ? `Q${m[1]}` : ''; };
      const grandTotal = seriesList.reduce((s: number, ser: any) =>
        s + (ser.data || []).reduce((ss: number, v: any) => ss + (Number(typeof v === 'object' ? v?.value : v) || 0), 0), 0);

      if (isQuarterly) {
        const headers = ['Year', 'Quarter', '%GT Sum', dimLabel, 'Sum of Total Sales'];
        const csvRows: any[] = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            const cat = categories[i] || '';
            const val = Number(typeof pt === 'object' ? pt?.value : pt) || 0;
            csvRows.push([
              extractYear(cat),
              extractQtr(cat),
              grandTotal > 0 ? Math.round((val / grandTotal) * 100) + '%' : '0%',
              ser.name,
              Math.round(val)
            ]);
          });
        });
        this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
      } else {
        const headers = ['Year', dimLabel, 'Sum of Total Sales'];
        const csvRows: any[] = [];
        seriesList.forEach((ser: any) => {
          (ser.data || []).forEach((pt: any, i: number) => {
            csvRows.push([categories[i] ?? '', ser.name, Number(typeof pt === 'object' ? pt?.value : pt) || 0]);
          });
        });
        this.mbarDownload(this.data?.widgetId || 'chart', headers, csvRows);
      }
    }
  }

  private mbarDownload(widgetId: string, headers: string[], rows: any[][]): void {
    downloadAsExcel(widgetId, headers, rows);
  }
}
